import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { chiffres, panelImages, googleReviews } from "../mock";

const useCountUp = (target, duration = 1600, start = false) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const startTime = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
};

const NumericRow = ({ item, active }) => {
  const v = useCountUp(item.value, 1800, active);
  const formatted = item.noFormat ? String(v) : v.toLocaleString("fr-FR");
  return (
    <div className="py-7 md:py-9 border-b border-white/15 grid grid-cols-[1fr_auto] gap-6 items-baseline">
      <span className="text-white/60 uppercase tracking-wider text-xs md:text-sm">
        {item.label}
      </span>
      <span
        className="font-display text-white leading-none"
        style={{ fontSize: "clamp(40px, 5.4vw, 92px)" }}
      >
        {formatted}
        {item.suffix}
      </span>
    </div>
  );
};

const TextRow = ({ text }) => (
  <div className="py-7 md:py-9 border-b border-white/15">
    <span
      className="font-display uppercase text-white leading-[0.95] block"
      style={{ fontSize: "clamp(32px, 4.4vw, 76px)" }}
    >
      {text}
    </span>
  </div>
);

const GoogleLogo = () => (
  <span className="inline-flex items-center gap-1 select-none" aria-label="Google">
    <span style={{ color: "#4285F4" }} className="font-semibold">G</span>
    <span style={{ color: "#EA4335" }} className="font-semibold">o</span>
    <span style={{ color: "#FBBC05" }} className="font-semibold">o</span>
    <span style={{ color: "#4285F4" }} className="font-semibold">g</span>
    <span style={{ color: "#34A853" }} className="font-semibold">l</span>
    <span style={{ color: "#EA4335" }} className="font-semibold">e</span>
  </span>
);

const Stars = ({ rating, size = 14 }) => {
  const rounded = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className="text-[#FBBC05]"
          fill={i <= rounded ? "currentColor" : "none"}
          strokeWidth={1.4}
          style={{ width: size, height: size }}
        />
      ))}
    </span>
  );
};

const ReviewCard = ({ r }) => (
  <div className="shrink-0 w-[85%] sm:w-[52%] md:w-[32%] snap-start bg-white rounded-lg p-5 md:p-6 shadow-sm border border-neutral-100">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[13px] font-medium tracking-wide">
        {r.initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-neutral-900 text-sm font-medium truncate">{r.author}</p>
          <GoogleLogo />
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Stars rating={r.rating} />
          <span className="text-neutral-400 text-[11px]">{r.date}</span>
        </div>
      </div>
    </div>
    <p className="mt-4 text-neutral-700 text-[13.5px] leading-relaxed line-clamp-4">
      {r.text}
    </p>
  </div>
);

const Chiffres = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible(true);
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="chiffres" ref={ref} className="relative overflow-hidden bg-[#111111] text-white" data-nav-theme="dark">
      <div className="absolute inset-0 opacity-20">
        <img src={panelImages.moderne} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/85 to-[#111111]/60" />
      </div>

      <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <h2
          className="font-display uppercase leading-[1.15] max-w-3xl"
          style={{ fontSize: "clamp(34px, 4.5vw, 76px)" }}
        >
          Des chiffres qui donnent du poids
          <span className="text-[#9F8B76]"> à nos engagements</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <div className="border-t border-white/15">
            {chiffres.map((c) =>
              c.kind === "text" ? (
                <TextRow key={c.id} text={c.value} />
              ) : (
                <NumericRow key={c.id} item={c} active={visible} />
              )
            )}
          </div>

          <div className="flex flex-col">
            <div className="bg-white rounded-xl p-6 md:p-7 shadow-md">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <GoogleLogo />
                  <span className="text-neutral-500 text-xs uppercase tracking-widest">Reviews</span>
                </div>
              </div>
              {googleReviews.reviews.length > 0 ? (
                <div className="mt-4 flex items-end gap-4">
                  <span className="font-display text-neutral-900 leading-none" style={{ fontSize: "clamp(52px, 6vw, 80px)" }}>
                    {googleReviews.rating.toFixed(1)}
                  </span>
                  <div className="pb-1.5">
                    <Stars rating={googleReviews.rating} size={18} />
                    <p className="text-neutral-500 text-[13px] mt-1">
                      Basé sur {googleReviews.count} avis Google
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-4 py-2">
                  <p className="text-neutral-500 text-[14px]">
                    Avis clients à venir prochainement
                  </p>
                </div>
              )}
            </div>

            {googleReviews.reviews.length > 0 && (
              <div className="mt-6 flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2">
                {googleReviews.reviews.map((r) => (
                  <ReviewCard key={r.id} r={r} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Chiffres;
